import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';

import { createStyles, createStyle } from 'utils/createStyle';
import { MobileDialog } from 'features/search/components/MobileDialog';
import classnames from 'classnames';
import { Group, Option, FilterGroup } from './types';
import { WalletSortAndFilterContext } from './context';
import { allCards } from './util';
import { stringSplitByIndex } from 'common/utils';

const footerBtnStyle = createStyle(
    'mt-5 w-full py-1 px-10 py-3 rounded-3xl bg-brand text-white font-semibold',
    {
        maxWidth: 280,
    }
);

interface ButtonProps {
    active: boolean;
    children: React.ReactNode;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    className?: string;
}

const Button = React.memo(({ active = false, children, onClick, className }: ButtonProps) => {
    const btnStyles = createStyles({
        btnStyles: [
            `text-brand ${active ? 'bg-pale-blue font-semibold' : 'bg-white'}`,
            {
                margin: '5px',
                border: '1px solid #005E9D',
                borderSizing: 'border-box',
                borderRadius: '5px',
                width: '140px',
                height: '50px',
                fontSize: 12,
                ':focus': {
                    outline: 'none',
                    boxShadow: 'none',
                },
            },
        ],
    });

    return (
        <button className={classnames(btnStyles.btnStyles, className)} onClick={onClick}>
            {children}
        </button>
    );
});

interface FilterDialogContextProps {
    selections: Option[];
    onClick: (_: Option) => void;
}

const FilterDialogContext = React.createContext<FilterDialogContextProps>({
    selections: [],
    onClick: () => null,
});

const optionIsSelected = (option: Option, selectedOptions: string[]) => {
    return selectedOptions.some((opt: string) => option.value === opt);
};

const CategoryOptionsRenderer = ({ categories }: { categories: Option[] }) => {
    const { selections, onClick } = useContext(FilterDialogContext);
    const selectedOptions = selections.map(({ value }: Option) => value);
    return (
        <div className="category-options-container grid grid-flow-rows justify-items-center grid-cols-2 mb-4">
            {categories.map((category: Option) => {
                const [first, second] = stringSplitByIndex(category.text, 12);
                return (
                    <Button
                        className={classnames(
                            category.imageUrl ? 'flex justify-around items-center px-2' : ''
                        )}
                        key={category.value}
                        active={optionIsSelected(category, selectedOptions)}
                        onClick={() => onClick(category)}
                    >
                        <span>
                            {category.imageUrl && <img width="40" src={category.imageUrl} />}
                        </span>
                        <span className="truncate">
                            {first}
                            {second && <br />}
                            {second}
                        </span>
                    </Button>
                );
            })}
        </div>
    );
};

const CategoryRenderer = ({ group }: { group: Group }) => {
    return (
        <div>
            <p className="font-semibold text-center text-grey-1 text-xl">{group.name}</p>
            <CategoryOptionsRenderer categories={group.options} />
        </div>
    );
};

const CategoriesRenderer = ({ groups }: { groups: Group[] }) => {
    return (
        <div>
            {groups.map((group: Group) => (
                <CategoryRenderer key={group.name} group={group} />
            ))}
        </div>
    );
};

interface MobileFilterDialogProps {
    groups: Group[];
}

export const MobileFilterDialog = ({ groups }: MobileFilterDialogProps) => {
    const {
        result,
        setActiveCardTypeFilter,
        activeCardTypeFilter,
        selectedBrands,
        setSelectedBrands,
        resolveCardTypeActiveFilters,
    } = useContext(WalletSortAndFilterContext);
    const [filters, setFilters] = useState<Option[]>([]);

    // just used to satisfy the MobileView interface but we don't need it to do anything
    const onApplyClicked = () => {};

    useEffect(() => {
        setFilters([...Array.from(activeCardTypeFilter), ...selectedBrands]);
    }, [activeCardTypeFilter, selectedBrands]);

    const onFilterClicked = useCallback(
        (option: Option) => {
            if (option.group === FilterGroup.brand) {
                setSelectedBrands((selectedFilters: Option[]) => {
                    if (
                        optionIsSelected(
                            option,
                            selectedFilters.map(({ value }: Option) => value)
                        )
                    ) {
                        // remove it from the set of filters
                        return selectedFilters.filter((opt: Option) => opt.value !== option.value);
                    }

                    // otherwise filter hasn't been selected and we should just add it to our selected filters
                    return [...selectedFilters, option];
                });
            } else {
                setActiveCardTypeFilter(resolveCardTypeActiveFilters(option as Option));
            }
        },
        [setActiveCardTypeFilter, setSelectedBrands, resolveCardTypeActiveFilters]
    );

    const clearAll = useCallback(() => {
        // We set `allCards` as the default filter view if user tries to clear
        // all the filters
        setActiveCardTypeFilter(new Set<Option>([allCards]));
        setSelectedBrands([]);
    }, [setActiveCardTypeFilter, setSelectedBrands]);

    const Header = useMemo(
        () => (
            <div>
                <span className="text-xs text-brand underline" onClick={clearAll}>
                    Clear All
                </span>
            </div>
        ),
        [clearAll]
    );

    return (
        <MobileDialog
            btnName="Filter"
            footer={(onClick: React.MouseEventHandler) => (
                <button className={footerBtnStyle} onClick={onClick}>
                    Show {result} Results
                </button>
            )}
            header={Header}
            styles={{ container: 'border-r-0 border-l-0' }}
            onApply={onApplyClicked}
        >
            <FilterDialogContext.Provider
                value={{
                    onClick: onFilterClicked,
                    selections: filters,
                }}
            >
                <CategoriesRenderer groups={groups} />
            </FilterDialogContext.Provider>
        </MobileDialog>
    );
};
